import { leesFoto } from "../pensionarrangement/arrangement";
import { useEffect, useMemo, useState, type ChangeEvent } from "react";
import { useCustom, useCustomMutation, useGetLocale, usePermissions, useTranslate } from "@refinedev/core";
import { Info, Pencil, Plus, Trash2, Upload, X } from "lucide-react";

import { formatEuro } from "../../i18n/format";
import {
  AccommodatieIcoon,
  DiersoortIcoon,
  ICONEN,
  meervoud,
  type AccommodatieType,
  type Prijsberekening,
} from "./accommodatie";
import "./AccommodatieBeheer.css";

type T = (key: string, options?: { args?: unknown[] }) => string;

interface Diersoort {
  id: number;
  /** De feed animals/reference/species noemt het label; oudere feeds naam. */
  label?: string | null;
  naam?: string | null;
}

const soortNaam = (d: Diersoort) => d.label ?? d.naam ?? String(d.id);

/** Een logo als data-URL; groter dan dit weigert de api. */
const LOGO_MAX_BYTES = 150_000;
const LOGO_TYPES = "image/png,image/jpeg,image/webp,image/gif,image/svg+xml";

/** Hoe een verblijf van dit type telt in de planning. */
const MODELLEN = ["PER_DIER", "PER_RESERVERING", "GROEP"] as const;
type Model = (typeof MODELLEN)[number];

/** Wat er bij het samen plaatsen meeweegt. */
const PLAATSINGSREGELS = ["geslacht", "castratie", "formaat", "leeftijd", "sociaal", "goedkeuring"] as const;
type Plaatsingsregel = (typeof PLAATSINGSREGELS)[number];

/** Buffer na vertrek, in minuten; leeg is geen buffer. */
const BUFFERS = ["", "15", "30", "60", "120", "240"];
const NOTITIE_MAX = 500;

interface Concept {
  id: number | null;
  diersoortId: string;
  naam: string;
  icoon: string | null;
  afbeelding: string | null;
  kleur: string;
  luxe: boolean;
  quarantaine: boolean;
  positie: number;
  omschrijving: string;
  foto: string | null;
  capaciteitsmodel: Model;
  maxDieren: string;
  minDieren: string;
  bufferMinuten: string;
  delenTussenReserveringen: boolean;
  alleenZelfdeEigenaar: boolean;
  voorkeurAutomatisch: boolean;
  actief: boolean;
  schoonmaakblok: boolean;
  regels: Record<Plaatsingsregel, boolean>;
  notities: string;
  /** Bij een nieuw type: hoeveel verblijven er meteen bij komen. */
  nieuweVerblijven: string;
}

const GEEN_REGELS: Record<Plaatsingsregel, boolean> = {
  geslacht: false,
  castratie: false,
  formaat: false,
  leeftijd: false,
  sociaal: false,
  goedkeuring: false,
};

const leegConcept = (diersoortId: string): Concept => ({
  id: null,
  diersoortId,
  naam: "",
  icoon: "home",
  afbeelding: null,
  kleur: "#2463EB",
  luxe: false,
  quarantaine: false,
  positie: 0,
  omschrijving: "",
  foto: null,
  capaciteitsmodel: "PER_DIER",
  maxDieren: "",
  minDieren: "",
  bufferMinuten: "",
  delenTussenReserveringen: false,
  alleenZelfdeEigenaar: true,
  voorkeurAutomatisch: false,
  actief: true,
  schoonmaakblok: false,
  regels: { ...GEEN_REGELS },
  notities: "",
  nieuweVerblijven: "",
});

/** Het aantal uit een veld; leeg of onzin telt als "niet ingesteld". */
const getal = (tekst: string): number | null => {
  const n = Number(tekst);
  return tekst.trim() === "" || Number.isNaN(n) ? null : Math.trunc(n);
};

/**
 * Beheer van de accommodatietypes van een vestiging: per diersoort de types met hun icoon of eigen logo, kleur,
 * en of ze luxe of quarantaine zijn. De prijs van een type staat hier alleen ter informatie; die stel je in bij
 * Producten › Pensiontarieven, en of het een dagprijs of een toeslag is bij Instellingen › Pension.
 *
 * <p>Staat in Instellingen › Pension, en opent ook vanuit de kennelplanning; daarom zonder eigen venster.
 */
export function AccommodatieBeheer({ vestigingId, onGewijzigd }: { vestigingId?: number; onGewijzigd?: () => void }) {
  const t = useTranslate() as T;
  const locale = useGetLocale()() ?? "nl";
  const query = vestigingId != null ? `?vestigingId=${vestigingId}` : "";
  const types = useCustom<AccommodatieType[]>({ url: `pension/accommodatietypes${query}`, method: "get" });
  const instelling = useCustom<{ prijsberekening: Prijsberekening }>({
    url: `pension/prijsberekening${query}`,
    method: "get",
    queryOptions: { retry: false },
  });
  const soorten = useCustom<Diersoort[]>({ url: "animals/reference/species", method: "get" });
  const { mutateAsync, isLoading: bezig } = useCustomMutation();
  // Wijzigen is beheer (ROLE_PENSION_ADMIN, net als de api); zonder dat recht is dit een overzicht.
  const { data: rechten } = usePermissions<string[]>();
  const magBeheren = (rechten ?? []).includes("ROLE_PENSION_ADMIN");

  const [concept, setConcept] = useState<Concept | null>(null);
  const [fout, setFout] = useState<string | null>(null);

  const lijst = useMemo(() => (Array.isArray(types.data?.data) ? types.data.data : []), [types.data]);
  const diersoorten = Array.isArray(soorten.data?.data) ? soorten.data.data : [];
  const berekening = instelling.data?.data?.prijsberekening ?? "PENSIONPRIJZEN";
  const ruimteOverzicht = useCustom<RuimteOverzicht[]>({ url: `pension/accommodatietypes/ruimtes${query}`, method: "get" });
  const alleRuimtes = useMemo(
    () => (Array.isArray(ruimteOverzicht.data?.data) ? ruimteOverzicht.data.data : []),
    [ruimteOverzicht.data],
  );
  const [meldingen, setMeldingen] = useState<Record<number, { fout: boolean; tekst: string }>>({});
  /** Het formulier voor een nieuwe ruimte staat open bij deze diersoort, met dit type voorgekozen. */
  const [nieuweRuimte, setNieuweRuimte] = useState<{ soortId: number; typeId: number } | null>(null);

  // Per diersoort de types en de ruimtes die er staan. Hond en kat altijd, ook zonder verblijven, zodat je ze
  // meteen kunt toevoegen; een ruimte zonder diersoort komt onder "zonder diersoort" (sleutel 0).
  const perSoort = useMemo(() => {
    const groepen = new Map<number, { naam: string | null; types: AccommodatieType[]; ruimtes: RuimteOverzicht[] }>();
    const groep = (id: number, naam: string | null) => {
      const g = groepen.get(id) ?? { naam, types: [], ruimtes: [] };
      if (!g.naam && naam) g.naam = naam;
      groepen.set(id, g);
      return g;
    };
    for (const d of diersoorten) {
      if (/^(hond|kat)$/i.test(soortNaam(d).trim())) groep(d.id, soortNaam(d));
    }
    for (const type of lijst) groep(type.diersoortId, type.diersoort).types.push(type);
    for (const r of alleRuimtes) {
      const type = r.typeId == null ? undefined : lijst.find((x) => x.id === r.typeId);
      groep(type?.diersoortId ?? r.diersoortId ?? 0, type?.diersoort ?? r.diersoort).ruimtes.push(r);
    }
    const volgorde = (id: number) => {
      const i = diersoorten.findIndex((d) => d.id === id);
      return id === 0 ? Number.MAX_SAFE_INTEGER : i < 0 ? diersoorten.length : i;
    };
    return [...groepen.entries()].sort(([a], [b]) => volgorde(a) - volgorde(b));
  }, [lijst, alleRuimtes, diersoorten]);

  const aantalTekst = (n: number) =>
    n === 1 ? t("pensionAccommodatie.overzicht.eenVerblijf") : t("pensionAccommodatie.overzicht.verblijven", { args: [n] });

  const ververs = () => {
    void types.refetch();
    void ruimteOverzicht.refetch();
    onGewijzigd?.();
  };

  const stil = { successNotification: false as const, errorNotification: false as const };
  const meldFout = (e: unknown) => {
    const status = (e as { statusCode?: number })?.statusCode;
    setFout(
      status === 409
        ? t("pensionAccommodatie.fout.bestaat")
        : status === 403
          ? t("pensionAccommodatie.fout.geenRecht")
          : status === 400
            ? t("pensionAccommodatie.fout.ongeldig")
            : t("pensionAccommodatie.fout.mislukt"),
    );
  };

  const bewaar = async () => {
    if (!concept) return;
    setFout(null);
    const values = {
      diersoortId: Number(concept.diersoortId),
      naam: concept.naam.trim(),
      icoon: concept.afbeelding ? null : concept.icoon,
      afbeelding: concept.afbeelding,
      kleur: concept.kleur,
      luxe: concept.luxe,
      quarantaine: concept.quarantaine,
      positie: concept.positie,
      omschrijving: concept.omschrijving.trim() === "" ? null : concept.omschrijving.trim(),
      foto: concept.foto,
      planning: {
        capaciteitsmodel: concept.capaciteitsmodel,
        maxDieren: getal(concept.maxDieren),
        minDieren: getal(concept.minDieren),
        bufferMinuten: getal(concept.bufferMinuten),
        delenTussenReserveringen: concept.delenTussenReserveringen,
        alleenZelfdeEigenaar: concept.alleenZelfdeEigenaar,
        voorkeurAutomatisch: concept.voorkeurAutomatisch,
        actief: concept.actief,
        schoonmaakblok: concept.schoonmaakblok,
        regelGeslacht: concept.regels.geslacht,
        regelCastratie: concept.regels.castratie,
        regelFormaat: concept.regels.formaat,
        regelLeeftijd: concept.regels.leeftijd,
        regelSociaal: concept.regels.sociaal,
        regelGoedkeuring: concept.regels.goedkeuring,
        notities: concept.notities.trim() === "" ? null : concept.notities.trim(),
      },
    };
    try {
      const antwoord = await mutateAsync({
        url: concept.id == null ? `pension/accommodatietypes${query}` : `pension/accommodatietypes/${concept.id}`,
        method: concept.id == null ? "post" : "put",
        values,
        ...stil,
      });
      const nieuwId = (antwoord?.data as { id?: number } | undefined)?.id;
      const aantal = getal(concept.nieuweVerblijven);
      if (concept.id == null && nieuwId != null && aantal != null && aantal > 0) {
        await mutateAsync({
          url: `pension/accommodatietypes/${nieuwId}/ruimtes`,
          method: "put",
          values: { groepId: null, naam: concept.naam.trim(), verblijven: aantal },
          ...stil,
        });
      }
      setConcept(null);
      ververs();
    } catch (e) {
      meldFout(e);
    }
  };

  /** Zet het aantal verblijven van een ruimte, of hangt een ruimte zonder type aan dit type. */
  const zetRuimte = async (typeId: number, groepId: number | null, verblijven: number, naam: string | null = null) => {
    setMeldingen({});
    try {
      await mutateAsync({
        url: `pension/accommodatietypes/${typeId}/ruimtes`,
        method: "put",
        values: { groepId, naam, verblijven },
        ...stil,
      });
      setMeldingen({ [typeId]: { fout: false, tekst: t("pensionAccommodatie.ruimtes.bewaard") } });
      ververs();
      return true;
    } catch (e) {
      const status = (e as { statusCode?: number })?.statusCode;
      setMeldingen({
        [typeId]: {
          fout: true,
          tekst: status === 409 ? t("pensionAccommodatie.ruimtes.inGebruik") : t("pensionAccommodatie.fout.mislukt"),
        },
      });
      return false;
    }
  };

  const verwijder = async (type: AccommodatieType) => {
    if (!window.confirm(t("pensionAccommodatie.verwijderen.vraag", { args: [type.naam] }))) return;
    setFout(null);
    try {
      await mutateAsync({ url: `pension/accommodatietypes/${type.id}`, method: "delete", values: {}, ...stil });
      ververs();
    } catch (e) {
      meldFout(e);
    }
  };

  const bewerk = (type: AccommodatieType) => {
    setFout(null);
    setConcept({
      id: type.id,
      diersoortId: String(type.diersoortId),
      naam: type.naam,
      icoon: type.icoon,
      afbeelding: type.afbeelding,
      kleur: type.kleur,
      luxe: type.luxe,
      quarantaine: type.quarantaine,
      positie: type.positie,
      omschrijving: type.omschrijving ?? "",
      foto: type.foto ?? null,
      capaciteitsmodel: (type.planning?.capaciteitsmodel as Model) ?? "PER_DIER",
      maxDieren: type.planning?.maxDieren == null ? "" : String(type.planning.maxDieren),
      minDieren: type.planning?.minDieren == null ? "" : String(type.planning.minDieren),
      bufferMinuten: type.planning?.bufferMinuten == null ? "" : String(type.planning.bufferMinuten),
      delenTussenReserveringen: type.planning?.delenTussenReserveringen ?? false,
      alleenZelfdeEigenaar: type.planning?.alleenZelfdeEigenaar ?? true,
      voorkeurAutomatisch: type.planning?.voorkeurAutomatisch ?? false,
      actief: type.planning?.actief ?? true,
      schoonmaakblok: type.planning?.schoonmaakblok ?? false,
      regels: {
        geslacht: type.planning?.regelGeslacht ?? false,
        castratie: type.planning?.regelCastratie ?? false,
        formaat: type.planning?.regelFormaat ?? false,
        leeftijd: type.planning?.regelLeeftijd ?? false,
        sociaal: type.planning?.regelSociaal ?? false,
        goedkeuring: type.planning?.regelGoedkeuring ?? false,
      },
      notities: type.planning?.notities ?? "",
      nieuweVerblijven: "",
    });
  };

  const prijsTekst = (type: AccommodatieType) => {
    const p = type.prijs;
    if (!p) return berekening === "ACCOMMODATIE" ? t("pensionAccommodatie.geenDagprijs") : null;
    const bedragen =
      p.bedragHoog != null && p.bedragHoog !== p.bedrag
        ? `${formatEuro(p.bedrag, locale)} / ${formatEuro(p.bedragHoog, locale)}`
        : formatEuro(p.bedrag, locale);
    return berekening === "ACCOMMODATIE"
      ? t("pensionAccommodatie.perDag", { args: [bedragen] })
      : t("pensionAccommodatie.toeslagPerDag", { args: [bedragen] });
  };

  return (
    <div className="acc-beheer">
      <section className="acc-kaart" aria-label={t("pensionAccommodatie.types.titel")}>
        <div className="acc-kop">
          <div>
            <h3>{t("pensionAccommodatie.types.titel")}</h3>
            <p>{t("pensionAccommodatie.types.uitleg")}</p>
          </div>
          {magBeheren && (
            <button
              type="button"
              className="acc-knop acc-knop--hoofd"
              disabled={bezig || diersoorten.length === 0}
              onClick={() => {
                setFout(null);
                setConcept(leegConcept(String(perSoort[0]?.[0] ?? diersoorten[0]?.id ?? "")));
              }}
            >
              <Plus size={15} aria-hidden="true" />
              {t("pensionAccommodatie.nieuw")}
            </button>
          )}
        </div>
        <p className="acc-leeg">{t("pensionAccommodatie.prijzenBijTarieven")}</p>
        {!magBeheren && <p className="acc-leeg">{t("pensionAccommodatie.alleenLezen")}</p>}

        {fout && (
          <p className="acc-fout" role="alert">
            {fout}
          </p>
        )}

        {concept && (
          <TypeFormulier
            concept={concept}
            diersoorten={diersoorten}
            t={t}
            bezig={bezig}
            onWijzig={setConcept}
            onFout={setFout}
            onBewaar={() => void bewaar()}
            onAnnuleer={() => {
              setConcept(null);
              void ruimteOverzicht.refetch();
            }}
          />
        )}

        {types.isLoading ? (
          <p className="acc-leeg">{t("common.loading")}</p>
        ) : types.isError ? (
          <p className="acc-fout">{t("pensionAccommodatie.fout.laden")}</p>
        ) : perSoort.length === 0 ? (
          <p className="acc-leeg">{t("pensionAccommodatie.types.leeg")}</p>
        ) : (
          perSoort.map(([soortId, groep]) => {
            const naam = soortId === 0 ? t("pensionAccommodatie.overzicht.overig") : meervoud(groep.naam);
            const totaal = groep.ruimtes.reduce((n, r) => n + r.verblijven, 0);
            // Een ruimte zonder diersoort mag aan elk type; anders alleen aan een type van dezelfde soort.
            const koppelTypes = soortId === 0 ? lijst : groep.types;
            return (
              <section key={soortId} className="acc-soort" aria-label={naam}>
                <div className="acc-soort__kop">
                  <h4>
                    {soortId !== 0 && <DiersoortIcoon naam={groep.naam} />}
                    {naam}
                    <span className="acc-teller">{aantalTekst(totaal)}</span>
                  </h4>
                  {magBeheren && soortId !== 0 && (
                    <div className="acc-soort__knoppen">
                      <button
                        type="button"
                        className="acc-knop"
                        aria-label={t("pensionAccommodatie.overzicht.ruimteToevoegenVoor", { args: [naam.toLowerCase()] })}
                        title={groep.types.length === 0 ? t("pensionAccommodatie.overzicht.eerstEenType") : undefined}
                        disabled={bezig || groep.types.length === 0}
                        onClick={() => setNieuweRuimte({ soortId, typeId: groep.types[0].id })}
                      >
                        <Plus size={14} aria-hidden="true" />
                        {t("pensionAccommodatie.overzicht.ruimteToevoegen")}
                      </button>
                      <button
                        type="button"
                        className="acc-knop"
                        aria-label={t("pensionAccommodatie.overzicht.typeToevoegenVoor", { args: [naam.toLowerCase()] })}
                        disabled={bezig}
                        onClick={() => {
                          setFout(null);
                          setConcept(leegConcept(String(soortId)));
                        }}
                      >
                        <Plus size={14} aria-hidden="true" />
                        {t("pensionAccommodatie.overzicht.typeToevoegen")}
                      </button>
                    </div>
                  )}
                </div>
                {nieuweRuimte?.soortId === soortId && (
                  <NieuweRuimte
                    key={nieuweRuimte.typeId}
                    types={groep.types}
                    beginType={nieuweRuimte.typeId}
                    bezig={bezig}
                    t={t}
                    onToevoegen={async (typeId, ruimteNaam, aantal) => {
                      if (await zetRuimte(typeId, null, aantal, ruimteNaam)) setNieuweRuimte(null);
                    }}
                    onAnnuleer={() => setNieuweRuimte(null)}
                  />
                )}
                {groep.types.length === 0 && groep.ruimtes.length === 0 && (
                  <p className="acc-leeg">{t("pensionAccommodatie.overzicht.leegSoort", { args: [naam.toLowerCase()] })}</p>
                )}
                {groep.types.length > 0 && (
                  <ul className="acc-lijst">
                    {groep.types.map((type) => {
                      const eigen = groep.ruimtes.filter((r) => r.typeId === type.id);
                      const melding = meldingen[type.id];
                      return (
                        <li key={type.id} className="acc-type">
                          <div className="acc-type__kop">
                            <span className="acc-icoon" style={{ background: `color-mix(in srgb, ${type.kleur} 14%, transparent)` }}>
                              <AccommodatieIcoon type={type} size={20} />
                            </span>
                            <span className="acc-naam">
                              <strong>{type.naam}</strong>
                              <span>
                                {[type.luxe ? t("pensionAccommodatie.luxe") : null, type.quarantaine ? t("pensionAccommodatie.quarantaine") : null, prijsTekst(type)]
                                  .filter(Boolean)
                                  .join(" · ")}
                              </span>
                            </span>
                            <span className="acc-teller">{aantalTekst(eigen.reduce((n, r) => n + r.verblijven, 0))}</span>
                            {magBeheren && (
                              <>
                                <button type="button" className="acc-knop acc-knop--icoon" aria-label={t("pensionAccommodatie.bewerken", { args: [type.naam] })}
                                  disabled={bezig} onClick={() => bewerk(type)}>
                                  <Pencil size={15} aria-hidden="true" />
                                </button>
                                <button type="button" className="acc-knop acc-knop--icoon" aria-label={t("pensionAccommodatie.verwijderen.knop", { args: [type.naam] })}
                                  disabled={bezig} onClick={() => void verwijder(type)}>
                                  <Trash2 size={15} aria-hidden="true" />
                                </button>
                              </>
                            )}
                          </div>
                          {eigen.length > 0 ? (
                            <ul className="acc-ruimtes acc-ruimtes--overzicht">
                              {eigen.map((r) => (
                                <RuimteAantal
                                  key={r.groepId}
                                  naam={r.naam}
                                  verblijven={r.verblijven}
                                  automatisch={!r.ingesteld}
                                  magBeheren={magBeheren}
                                  bezig={bezig}
                                  t={t}
                                  onBijwerken={(n) => void zetRuimte(type.id, r.groepId, n)}
                                />
                              ))}
                            </ul>
                          ) : (
                            <p className="acc-leeg acc-type__leeg">{t("pensionAccommodatie.overzicht.geenVerblijven")}</p>
                          )}
                          {magBeheren && (
                            <p className="acc-type__leeg">
                              <button
                                type="button"
                                className="acc-link"
                                disabled={bezig}
                                onClick={() => setNieuweRuimte({ soortId, typeId: type.id })}
                              >
                                <Plus size={13} aria-hidden="true" />
                                {t("pensionAccommodatie.overzicht.ruimteBijType", { args: [type.naam] })}
                              </button>
                            </p>
                          )}
                          {melding && (
                            <p className={melding.fout ? "acc-fout" : "acc-hint"} role={melding.fout ? "alert" : "status"}>
                              {melding.tekst}
                            </p>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
                {groep.ruimtes.some((r) => r.typeId == null) && (
                  <div className="acc-los">
                    <h5>{t("pensionAccommodatie.overzicht.los.titel")}</h5>
                    <p className="acc-hint">{t("pensionAccommodatie.overzicht.los.uitleg")}</p>
                    <ul className="acc-ruimtes acc-ruimtes--overzicht">
                      {groep.ruimtes
                        .filter((r) => r.typeId == null)
                        .map((r) => (
                          <li key={r.groepId} className="acc-los__regel">
                            <strong>{r.naam}</strong>
                            <span className="acc-hint">{aantalTekst(r.verblijven)}</span>
                            {magBeheren && koppelTypes.length > 0 && (
                              <select
                                aria-label={t("pensionAccommodatie.overzicht.los.koppelVan", { args: [r.naam ?? ""] })}
                                value=""
                                disabled={bezig}
                                onChange={(e) => {
                                  const typeId = Number(e.target.value);
                                  if (typeId) void zetRuimte(typeId, r.groepId, r.verblijven);
                                }}
                              >
                                <option value="">{t("pensionAccommodatie.overzicht.los.kies")}</option>
                                {koppelTypes.map((type) => (
                                  <option key={type.id} value={type.id}>
                                    {type.naam}
                                  </option>
                                ))}
                              </select>
                            )}
                          </li>
                        ))}
                    </ul>
                  </div>
                )}
              </section>
            );
          })
        )}
      </section>
    </div>
  );
}

/** Een ruimte in het overzicht van de vestiging; zonder typeId hoort hij nog bij geen type. */
interface RuimteOverzicht {
  groepId: number;
  naam: string | null;
  diersoortId: number | null;
  diersoort: string | null;
  typeId: number | null;
  /** Zo ingesteld; anders leidt de planning het type af uit de naam van de ruimte. */
  ingesteld: boolean;
  verblijven: number;
}

/** Een nieuwe ruimte bij een type van deze diersoort: naam, type en aantal verblijven. */
function NieuweRuimte({
  types,
  beginType,
  bezig,
  t,
  onToevoegen,
  onAnnuleer,
}: {
  types: AccommodatieType[];
  beginType: number;
  bezig: boolean;
  t: T;
  onToevoegen: (typeId: number, naam: string, aantal: number) => void;
  onAnnuleer: () => void;
}) {
  const [typeId, setTypeId] = useState(String(beginType));
  const [naam, setNaam] = useState("");
  const [aantal, setAantal] = useState("");
  const type = types.find((x) => String(x.id) === typeId);
  const geldig = Number(aantal) > 0 && Number(aantal) <= 99;
  return (
    <form
      className="acc-ruimte-nieuw acc-ruimte-nieuw--overzicht"
      aria-label={t("pensionAccommodatie.overzicht.ruimteToevoegen")}
      onSubmit={(e) => {
        e.preventDefault();
        if (geldig && type) onToevoegen(type.id, naam.trim() || type.naam, Math.trunc(Number(aantal)));
      }}
    >
      <label className="acc-veld">
        <span>{t("pensionAccommodatie.ruimtes.nieuweNaam")}</span>
        <input type="text" maxLength={60} placeholder={type?.naam} value={naam} onChange={(e) => setNaam(e.target.value)} />
      </label>
      <label className="acc-veld">
        <span>{t("pensionAccommodatie.overzicht.type")}</span>
        <select value={typeId} onChange={(e) => setTypeId(e.target.value)}>
          {types.map((x) => (
            <option key={x.id} value={x.id}>
              {x.naam}
            </option>
          ))}
        </select>
      </label>
      <label className="acc-veld acc-veld--smal">
        <span>{t("pensionAccommodatie.ruimtes.aantal")}</span>
        <input type="number" min={1} max={99} value={aantal} onChange={(e) => setAantal(e.target.value)} />
      </label>
      <div className="acc-ruimte-nieuw__knoppen">
        <button type="button" className="acc-knop" onClick={onAnnuleer}>
          {t("common.cancel")}
        </button>
        <button type="submit" className="acc-knop acc-knop--hoofd" disabled={bezig || !geldig}>
          <Plus size={14} aria-hidden="true" />
          {t("pensionAccommodatie.ruimtes.toevoegen")}
        </button>
      </div>
    </form>
  );
}

/** Een ruimte met zijn aantal verblijven; wie beheert, past het aantal hier meteen aan. */
function RuimteAantal({
  naam,
  automatisch = false,
  verblijven,
  magBeheren,
  bezig,
  t,
  onBijwerken,
}: {
  naam: string | null;
  automatisch?: boolean;
  verblijven: number;
  magBeheren: boolean;
  bezig: boolean;
  t: T;
  onBijwerken: (n: number) => void;
}) {
  const [waarde, setWaarde] = useState(String(verblijven));
  useEffect(() => setWaarde(String(verblijven)), [verblijven]);
  const label = t("pensionAccommodatie.ruimtes.aantalVan", { args: [naam ?? ""] });
  const gewijzigd = waarde.trim() !== "" && waarde !== String(verblijven);
  return (
    <li>
      <span className="acc-ruimte__naam">
        <strong>{naam}</strong>
        {automatisch && (
          <span className="acc-chip" title={t("pensionAccommodatie.overzicht.automatischUitleg")}>
            {t("pensionAccommodatie.overzicht.automatisch")}
          </span>
        )}
      </span>
      {magBeheren ? (
        <input type="number" min={0} max={99} aria-label={label} value={waarde} onChange={(e) => setWaarde(e.target.value)} />
      ) : (
        <span>{verblijven}</span>
      )}
      <span className="acc-hint">{t("pensionAccommodatie.ruimtes.verblijven")}</span>
      {magBeheren && (
        <button type="button" className="acc-knop" disabled={!gewijzigd || bezig} onClick={() => onBijwerken(Number(waarde))}>
          {t("pensionAccommodatie.ruimtes.bijwerken")}
        </button>
      )}
    </li>
  );
}

interface Ruimte {
  groepId: number;
  naam: string | null;
  verblijven: number;
}

/**
 * Hoeveel verblijven er van dit type zijn. Een type hangt aan een of meer ruimtes; per ruimte zet je het aantal,
 * en je kunt een ruimte toevoegen. Bij een nieuw type geef je alleen het aantal: de ruimte krijgt de naam van het
 * type.
 */
function RuimtesBlok({
  typeId,
  typeNaam,
  soortLabel,
  nieuwAantal,
  onNieuwAantal,
  t,
}: {
  typeId: number | null;
  typeNaam: string;
  soortLabel: string;
  nieuwAantal: string;
  onNieuwAantal: (v: string) => void;
  t: T;
}) {
  const lijst = useCustom<Ruimte[]>({
    url: `pension/accommodatietypes/${typeId}/ruimtes`,
    method: "get",
    queryOptions: { enabled: typeId != null },
  });
  const { mutateAsync, isLoading: bezig } = useCustomMutation();
  const [aantallen, setAantallen] = useState<Record<number, string>>({});
  const [nieuwNaam, setNieuwNaam] = useState("");
  const [nieuwTal, setNieuwTal] = useState("");
  const [melding, setMelding] = useState<{ fout: boolean; tekst: string } | null>(null);
  const ruimtes = Array.isArray(lijst.data?.data) ? lijst.data!.data : [];
  const totaal = ruimtes.reduce((n, r) => n + r.verblijven, 0);

  const zet = async (groepId: number | null, verblijven: number, naam?: string) => {
    setMelding(null);
    try {
      await mutateAsync({
        url: `pension/accommodatietypes/${typeId}/ruimtes`,
        method: "put",
        values: { groepId, naam: naam ?? null, verblijven },
        successNotification: false,
        errorNotification: false,
      });
      setAantallen({});
      setNieuwNaam("");
      setNieuwTal("");
      setMelding({ fout: false, tekst: t("pensionAccommodatie.ruimtes.bewaard") });
      void lijst.refetch();
    } catch (e) {
      const status = (e as { statusCode?: number }).statusCode;
      setMelding({
        fout: true,
        tekst: status === 409 ? t("pensionAccommodatie.ruimtes.inGebruik") : t("pensionAccommodatie.fout.mislukt"),
      });
    }
  };

  return (
    <fieldset className="acc-blok">
      <legend>{t("pensionAccommodatie.ruimtes.titel")}</legend>
      {typeId == null ? (
        <label className="acc-veld acc-veld--smal">
          <span>{t("pensionAccommodatie.ruimtes.aantal")}</span>
          <input type="number" min={0} max={99} value={nieuwAantal} onChange={(e) => onNieuwAantal(e.target.value)} />
          <small className="acc-hint">{t("pensionAccommodatie.ruimtes.nieuwUitleg", { args: [typeNaam || "…"] })}</small>
        </label>
      ) : (
        <>
          <p className="acc-hint">
            {t("pensionAccommodatie.ruimtes.uitleg", { args: [totaal, meervoud(soortLabel).toLowerCase()] })}
          </p>
          {ruimtes.length > 0 && (
            <ul className="acc-ruimtes">
              {ruimtes.map((r) => {
                const waarde = aantallen[r.groepId] ?? String(r.verblijven);
                const gewijzigd = waarde !== String(r.verblijven) && waarde.trim() !== "";
                return (
                  <li key={r.groepId}>
                    <strong>{r.naam}</strong>
                    <label>
                      <span className="acc-verborgen">{t("pensionAccommodatie.ruimtes.aantalVan", { args: [r.naam ?? ""] })}</span>
                      <input
                        type="number"
                        min={0}
                        max={99}
                        aria-label={t("pensionAccommodatie.ruimtes.aantalVan", { args: [r.naam ?? ""] })}
                        value={waarde}
                        onChange={(e) => setAantallen((a) => ({ ...a, [r.groepId]: e.target.value }))}
                      />
                    </label>
                    <span className="acc-hint">{t("pensionAccommodatie.ruimtes.verblijven")}</span>
                    <button
                      type="button"
                      className="acc-knop"
                      disabled={!gewijzigd || bezig}
                      onClick={() => void zet(r.groepId, Number(waarde))}
                    >
                      {t("pensionAccommodatie.ruimtes.bijwerken")}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          <div className="acc-ruimte-nieuw">
            <label className="acc-veld">
              <span>{t("pensionAccommodatie.ruimtes.nieuweNaam")}</span>
              <input type="text" maxLength={60} placeholder={typeNaam} value={nieuwNaam} onChange={(e) => setNieuwNaam(e.target.value)} />
            </label>
            <label className="acc-veld acc-veld--smal">
              <span>{t("pensionAccommodatie.ruimtes.aantal")}</span>
              <input type="number" min={1} max={99} value={nieuwTal} onChange={(e) => setNieuwTal(e.target.value)} />
            </label>
            <button
              type="button"
              className="acc-knop"
              disabled={bezig || !(Number(nieuwTal) > 0)}
              onClick={() => void zet(null, Number(nieuwTal), nieuwNaam.trim() || typeNaam)}
            >
              <Plus size={14} aria-hidden="true" />
              {t("pensionAccommodatie.ruimtes.toevoegen")}
            </button>
          </div>
          {melding && (
            <p className={melding.fout ? "acc-fout" : "acc-hint"} role={melding.fout ? "alert" : "status"}>
              {melding.tekst}
            </p>
          )}
        </>
      )}
    </fieldset>
  );
}

/** Een schakelaar met zijn label, zoals in de filters van de planning. */
function Schakel({
  aan,
  uit,
  label,
  onZet,
  t,
}: {
  aan: boolean;
  uit?: boolean;
  label: string;
  onZet: (waarde: boolean) => void;
  t: T;
}) {
  return (
    <label className="acc-schakelrij">
      <span className="acc-schakel">
        <input type="checkbox" role="switch" checked={aan} disabled={uit} onChange={(e) => onZet(e.target.checked)} />
        <span className="acc-schakel__baan" aria-hidden="true" />
      </span>
      <span>
        {label}
        <small>{t(aan ? "common.yes" : "common.no")}</small>
      </span>
    </label>
  );
}

function TypeFormulier({
  concept,
  diersoorten,
  t,
  bezig,
  onWijzig,
  onFout,
  onBewaar,
  onAnnuleer,
}: {
  concept: Concept;
  diersoorten: Diersoort[];
  t: T;
  bezig: boolean;
  onWijzig: (concept: Concept) => void;
  onFout: (fout: string | null) => void;
  onBewaar: () => void;
  onAnnuleer: () => void;
}) {
  const zet = <K extends keyof Concept>(sleutel: K, waarde: Concept[K]) => onWijzig({ ...concept, [sleutel]: waarde });

  const kiesLogo = (e: ChangeEvent<HTMLInputElement>) => {
    const bestand = e.target.files?.[0];
    e.target.value = "";
    if (!bestand) return;
    if (bestand.size > LOGO_MAX_BYTES || !LOGO_TYPES.split(",").includes(bestand.type)) {
      onFout(t("pensionAccommodatie.fout.logo"));
      return;
    }
    const lezer = new FileReader();
    lezer.onload = () => {
      onFout(null);
      zet("afbeelding", String(lezer.result));
    };
    lezer.readAsDataURL(bestand);
  };

  const kiesFoto = async (e: ChangeEvent<HTMLInputElement>) => {
    const bestand = e.target.files?.[0];
    e.target.value = "";
    if (!bestand) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(bestand.type)) {
      onFout(t("pensionAccommodatie.fout.foto"));
      return;
    }
    onFout(null);
    const foto = await leesFoto(bestand);
    onWijzig({ ...concept, foto });
  };

  const kanBewaren = concept.naam.trim() !== "" && concept.diersoortId !== "";
  const perDier = concept.capaciteitsmodel === "PER_DIER";
  const soortLabel = soortNaam(diersoorten.find((d) => String(d.id) === concept.diersoortId) ?? { id: 0 });
  // Wat de capaciteit doet in drie gevallen, zodat te zien is wat het model betekent.
  const max = getal(concept.maxDieren) ?? 99;
  const voorbeeld = [
    {
      situatie: t("pensionAccommodatie.voorbeeld.een", { args: [soortLabel.toLowerCase()] }),
      bezet: t("pensionAccommodatie.voorbeeld.ruimtes", { args: [1] }),
    },
    {
      situatie: t("pensionAccommodatie.voorbeeld.drie", { args: [meervoud(soortLabel).toLowerCase()] }),
      bezet: t("pensionAccommodatie.voorbeeld.ruimtes", { args: [perDier ? 3 : Math.max(1, Math.ceil(3 / Math.min(3, max))) ] }),
    },
    {
      situatie: t("pensionAccommodatie.voorbeeld.twee", { args: [meervoud(soortLabel).toLowerCase()] }),
      bezet: t("pensionAccommodatie.voorbeeld.ruimtes", {
        args: [perDier ? 3 : concept.capaciteitsmodel === "GROEP" ? 1 : 2],
      }),
    },
  ];
  const Gekozen = ICONEN[(concept.icoon ?? "home") as keyof typeof ICONEN] ?? ICONEN.home;

  return (
    <form
      className="acc-formulier"
      aria-label={concept.id == null ? t("pensionAccommodatie.nieuw") : t("pensionAccommodatie.bewerken", { args: [concept.naam] })}
      onSubmit={(e) => {
        e.preventDefault();
        if (kanBewaren) onBewaar();
      }}
    >
      <fieldset className="acc-blok">
        <legend>{t("pensionAccommodatie.algemeen.titel")}</legend>
        <div className="acc-algemeen">
          <label className="acc-veld">
            <span>{t("pensionAccommodatie.veld.naam")}</span>
            <input type="text" required maxLength={60} value={concept.naam} onChange={(e) => zet("naam", e.target.value)} />
          </label>
          <label className="acc-veld">
            <span>{t("pensionAccommodatie.veld.diersoort")}</span>
            <select value={concept.diersoortId} disabled={concept.id != null} onChange={(e) => zet("diersoortId", e.target.value)}>
              {diersoorten.map((d) => (
                <option key={d.id} value={d.id}>
                  {soortNaam(d)}
                </option>
              ))}
            </select>
          </label>

          <div className="acc-veld">
            <span>{t("pensionAccommodatie.veld.icoon")}</span>
            <div className="acc-icoonrij">
              <span className="acc-icoonvoorbeeld" style={{ color: concept.kleur }} aria-hidden="true">
                {concept.afbeelding ? <img src={concept.afbeelding} alt="" /> : <Gekozen size={20} aria-hidden="true" />}
              </span>
              <select
                aria-label={t("pensionAccommodatie.veld.icoon")}
                value={concept.afbeelding ? "" : (concept.icoon ?? "home")}
                disabled={!!concept.afbeelding}
                onChange={(e) => onWijzig({ ...concept, icoon: e.target.value, afbeelding: null })}
              >
                {Object.keys(ICONEN).map((sleutel) => (
                  <option key={sleutel} value={sleutel}>
                    {t(`pensionAccommodatie.icoon.${sleutel}`)}
                  </option>
                ))}
              </select>
              <label className="acc-kleur" title={t("pensionAccommodatie.veld.kleur")}>
                <span className="acc-verborgen">{t("pensionAccommodatie.veld.kleur")}</span>
                <input type="color" value={concept.kleur} onChange={(e) => zet("kleur", e.target.value.toUpperCase())} />
              </label>
            </div>
          </div>
          <div className="acc-veld">
            <span>{t("pensionAccommodatie.veld.logo")}</span>
            <div className="acc-uploadrij">
              {concept.afbeelding && <img className="acc-miniatuur" src={concept.afbeelding} alt={t("pensionAccommodatie.veld.logo")} />}
              <label className="acc-knop">
                <Upload size={14} aria-hidden="true" />
                {t("pensionAccommodatie.logo.kies")}
                <input type="file" accept={LOGO_TYPES} className="acc-verborgen" onChange={kiesLogo} />
              </label>
              {concept.afbeelding && (
                <button type="button" className="acc-knop" onClick={() => zet("afbeelding", null)}>
                  <X size={14} aria-hidden="true" />
                  {t("pensionAccommodatie.logo.weg")}
                </button>
              )}
            </div>
            <small className="acc-hint">{t("pensionAccommodatie.logo.uitleg")}</small>
          </div>

          <label className="acc-veld acc-veld--breed">
            <span>{t("pensionAccommodatie.veld.omschrijving")}</span>
            <textarea rows={2} maxLength={250} value={concept.omschrijving} onChange={(e) => zet("omschrijving", e.target.value)} />
            <small className="acc-hint">{t("pensionAccommodatie.omschrijving.uitleg")}</small>
          </label>
          <div className="acc-veld acc-veld--breed">
            <span>{t("pensionAccommodatie.veld.foto")}</span>
            <div className="acc-uploadrij">
              {concept.foto && <img className="acc-miniatuur acc-miniatuur--foto" src={concept.foto} alt={t("pensionAccommodatie.veld.foto")} />}
              <label className="acc-knop">
                <Upload size={14} aria-hidden="true" />
                {t("pensionAccommodatie.foto.kies")}
                <input type="file" accept="image/png,image/jpeg,image/webp" className="acc-verborgen" onChange={(e) => void kiesFoto(e)} />
              </label>
              {concept.foto && (
                <button type="button" className="acc-knop" onClick={() => zet("foto", null)}>
                  <X size={14} aria-hidden="true" />
                  {t("pensionAccommodatie.foto.weg")}
                </button>
              )}
              <small className="acc-hint">{t("pensionAccommodatie.foto.uitleg")}</small>
            </div>
          </div>
        </div>
      </fieldset>

      <RuimtesBlok
        typeId={concept.id}
        typeNaam={concept.naam}
        soortLabel={soortLabel}
        nieuwAantal={concept.nieuweVerblijven}
        onNieuwAantal={(v) => zet("nieuweVerblijven", v)}
        t={t}
      />

      <fieldset className="acc-blok">
        <legend>{t("pensionAccommodatie.model.titel")}</legend>
        <div className="acc-modellen" role="radiogroup" aria-label={t("pensionAccommodatie.model.titel")}>
          {MODELLEN.map((m) => (
            <label key={m} className="acc-model" data-gekozen={concept.capaciteitsmodel === m ? "" : undefined}>
              <input
                type="radio"
                name="acc-capaciteitsmodel"
                checked={concept.capaciteitsmodel === m}
                onChange={() => zet("capaciteitsmodel", m)}
              />
              <span>
                <strong>{t(`pensionAccommodatie.model.${m}`)}</strong>
                <small>{t(`pensionAccommodatie.model.${m}.uitleg`, { args: [meervoud(soortLabel)] })}</small>
              </span>
            </label>
          ))}
        </div>
        <p className="acc-melding">
          <Info size={15} aria-hidden="true" />
          {t(`pensionAccommodatie.model.${concept.capaciteitsmodel}.telt`)}
        </p>
      </fieldset>

      <fieldset className="acc-blok">
        <legend>{t("pensionAccommodatie.regels.titel")}</legend>
        <div className="acc-velden">
          <label>
            <span>{t("pensionAccommodatie.regels.max", { args: [meervoud(soortLabel)] })}</span>
            <input
              type="number"
              min={1}
              max={20}
              disabled={perDier}
              value={perDier ? "1" : concept.maxDieren}
              onChange={(e) => zet("maxDieren", e.target.value)}
            />
          </label>
          <label>
            <span>{t("pensionAccommodatie.regels.min", { args: [meervoud(soortLabel)] })}</span>
            <input
              type="number"
              min={1}
              max={20}
              disabled={perDier}
              value={perDier ? "1" : concept.minDieren}
              onChange={(e) => zet("minDieren", e.target.value)}
            />
          </label>
          <label>
            <span>{t("pensionAccommodatie.regels.buffer")}</span>
            <select value={concept.bufferMinuten} onChange={(e) => zet("bufferMinuten", e.target.value)}>
              {BUFFERS.map((b) => (
                <option key={b || "geen"} value={b}>
                  {b === "" ? t("pensionAccommodatie.regels.buffer.geen") : t("pensionAccommodatie.regels.buffer.minuten", { args: [Number(b)] })}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="acc-schakels">
          <Schakel
            aan={concept.delenTussenReserveringen}
            uit={perDier}
            label={t("pensionAccommodatie.regels.delen")}
            onZet={(v) => zet("delenTussenReserveringen", v)}
            t={t}
          />
          <Schakel
            aan={concept.alleenZelfdeEigenaar}
            uit={perDier}
            label={t("pensionAccommodatie.regels.zelfdeEigenaar", { args: [meervoud(soortLabel)] })}
            onZet={(v) => zet("alleenZelfdeEigenaar", v)}
            t={t}
          />
          <Schakel
            aan={concept.voorkeurAutomatisch}
            label={t("pensionAccommodatie.regels.voorkeur")}
            onZet={(v) => zet("voorkeurAutomatisch", v)}
            t={t}
          />
          <Schakel aan={concept.actief} label={t("pensionAccommodatie.regels.actief")} onZet={(v) => zet("actief", v)} t={t} />
          <Schakel
            aan={concept.schoonmaakblok}
            label={t("pensionAccommodatie.regels.schoonmaak")}
            onZet={(v) => zet("schoonmaakblok", v)}
            t={t}
          />
        </div>
      </fieldset>

      <fieldset className="acc-blok">
        <legend>{t("pensionAccommodatie.plaatsing.titel")}</legend>
        <p className="acc-leeg">{t("pensionAccommodatie.plaatsing.uitleg")}</p>
        <div className="acc-plaatsingsregels">
          {PLAATSINGSREGELS.map((regel) => (
            <label key={regel} className="acc-regelkaart" data-aan={concept.regels[regel] ? "" : undefined}>
              <span className="acc-regelkaart__naam">{t(`pensionAccommodatie.plaatsing.${regel}`)}</span>
              <span className="acc-schakel">
                <input
                  type="checkbox"
                  role="switch"
                  checked={concept.regels[regel]}
                  disabled={perDier && regel !== "goedkeuring"}
                  onChange={(e) => onWijzig({ ...concept, regels: { ...concept.regels, [regel]: e.target.checked } })}
                />
                <span className="acc-schakel__baan" aria-hidden="true" />
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      <fieldset className="acc-blok acc-blok--voorbeeld">
        <legend>{t("pensionAccommodatie.voorbeeld.titel")}</legend>
        <table className="acc-voorbeeld">
          <thead>
            <tr>
              <th scope="col">{t("pensionAccommodatie.voorbeeld.situatie")}</th>
              <th scope="col">{t("pensionAccommodatie.voorbeeld.bezet")}</th>
            </tr>
          </thead>
          <tbody>
            {voorbeeld.map((rij) => (
              <tr key={rij.situatie}>
                <td>{rij.situatie}</td>
                <td>{rij.bezet}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="acc-samenvatting">
          <strong>{t("pensionAccommodatie.samenvatting.titel")}</strong>
          <dl>
            <div>
              <dt>{t("pensionAccommodatie.samenvatting.teltOp")}</dt>
              <dd>{t(`pensionAccommodatie.samenvatting.${concept.capaciteitsmodel}`)}</dd>
            </div>
            <div>
              <dt>{t("pensionAccommodatie.samenvatting.max", { args: [meervoud(soortLabel)] })}</dt>
              <dd>{perDier ? "1" : (concept.maxDieren || t("pensionAccommodatie.samenvatting.geenGrens"))}</dd>
            </div>
            <div>
              <dt>{t("pensionAccommodatie.samenvatting.samen")}</dt>
              <dd>
                {perDier
                  ? t("pensionAccommodatie.samenvatting.nooit")
                  : concept.delenTussenReserveringen
                    ? t("pensionAccommodatie.samenvatting.ookAndere")
                    : t("pensionAccommodatie.samenvatting.zelfdeReservering")}
              </dd>
            </div>
          </dl>
        </div>
      </fieldset>

      <label className="acc-notities">
        <span>{t("pensionAccommodatie.notities.titel")}</span>
        <textarea
          rows={2}
          aria-label={t("pensionAccommodatie.notities.titel")}
          maxLength={NOTITIE_MAX}
          placeholder={t("pensionAccommodatie.notities.uitleg")}
          value={concept.notities}
          onChange={(e) => zet("notities", e.target.value)}
        />
        <small>{concept.notities.length}/{NOTITIE_MAX}</small>
      </label>

      <div className="acc-voet">
        <button type="button" className="acc-knop" onClick={onAnnuleer} disabled={bezig}>
          {t("common.cancel")}
        </button>
        <button type="submit" className="acc-knop acc-knop--hoofd" disabled={bezig || !kanBewaren}>
          {t("common.save")}
        </button>
      </div>
    </form>
  );
}
